import { type ReactElement } from 'react';

import { Group } from './Group.js';
import type { OptionProps } from './Option.js';
import { Option } from './Option.js';
import { _isOption, safeStringify } from '../hooks/use-select-options.js';
import type { BaseGroup, BaseOption } from '../types.js';

/**
 * This function will sort the given children (options or groups) by their selection state and will store
 * the index in a map in order to apply the sorting in a quick fashion again.
 * @internal
 */
export function sortSelectedOptions<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(
  childrenArray: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >,
): [
  sortedChildren: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortingMap: WeakMap<any, number>,
] {
  const sortedChildren: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  > = [];
  const sortingMap = new Map();
  let insertedFront = 0;

  // Sort the children based on their selection state
  for (const optionOrGroup of childrenArray) {
    if (optionOrGroup.type === Option) {
      if (optionOrGroup.props.isSelected === true) {
        sortedChildren.splice(insertedFront, 0, optionOrGroup);
        insertedFront++;
        continue;
      } else {
        sortedChildren.push(optionOrGroup);
      }
    } else if (optionOrGroup.type === Group) {
      const hasSelectedChildOptions = optionOrGroup.props.options.some(
        (option) => _isOption(option) && option.isSelected,
      );

      if (!hasSelectedChildOptions) {
        sortedChildren.push(optionOrGroup);
        continue;
      }

      sortedChildren.splice(insertedFront, 0, optionOrGroup);
      insertedFront++;
      continue;
    }
  }

  // Iterate through the sorted list and store the index that it is currently at.
  for (const [index, optionOrGroup] of sortedChildren.entries()) {
    sortingMap.set(getOptionOrGroupValue(optionOrGroup), index);
  }

  return [sortedChildren, sortingMap];
}

/**
 * This function will apply the sorting stored in the passed map in order to.
 */
export function applyStoredSelectionSortingOptions<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(
  childrenArray: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortingMap: WeakMap<any, number>,
): [
  sortedChildren: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortingMap?: WeakMap<any, number>,
] {
  const sortedChildren: Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  > = [];

  let sortingMiss = false;

  for (const optionOrGroup of childrenArray) {
    const index = sortingMap.get(getOptionOrGroupValue(optionOrGroup));
    if (index === undefined) {
      sortingMiss = true;
      break;
    }
    sortedChildren[index] = optionOrGroup;
  }

  if (sortingMiss) {
    return sortSelectedOptions(childrenArray);
  }

  return [sortedChildren];
}

function getOptionOrGroupValue<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(
  optionOrGroup: ReactElement<
    OptionProps<BaseOption<OptionValue>, isMulti, Group>
  >,
): string {
  if (optionOrGroup.props.data['value']) {
    const stringifiedValue = safeStringify(
      optionOrGroup.props.data['value'],
      false,
    );

    if (stringifiedValue) {
      return stringifiedValue;
    }

    return `option-${optionOrGroup.props.data['value']}`;
  }

  return `group-${optionOrGroup.props.data['label']}`;
}
